import { format, subDays } from 'date-fns'
import { useMemo, useState } from 'react'
import type { DateRange } from 'react-day-picker'
import { KPICard, TokensOverTimeChart } from '@/components/analytics'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { useOrganizationTokenUsage } from '@/hooks/useAnalytics'
import { formatTokenCount } from '@/lib/formatTokenCount'
import type { DateRangeParams } from '@/types/analytics'

const DATE_FORMAT = 'yyyy-MM-dd'

const getDefaultDateRange = (): DateRange => ({
  from: subDays(new Date(), 30),
  to: new Date(),
})

export function UsageSection() {
  const [dateRange, setDateRange] = useState<DateRange | undefined>(
    getDefaultDateRange()
  )

  const dateParams: DateRangeParams = useMemo(() => {
    const now = new Date()
    const defaultFrom = subDays(now, 30)

    const from = dateRange?.from ?? defaultFrom
    const to = dateRange?.to ?? now

    const validFrom = from <= to ? from : to
    const validTo = from <= to ? to : from

    const maxFrom = subDays(validTo, 365)
    const boundedFrom = validFrom < maxFrom ? maxFrom : validFrom

    return {
      startDate: format(boundedFrom, DATE_FORMAT),
      endDate: format(validTo, DATE_FORMAT),
    }
  }, [dateRange])

  const dateRangeLabel = useMemo(() => {
    if (!dateRange?.from) return ''

    const fromStr = format(dateRange.from, 'LLL d, yyyy')
    const toStr = dateRange.to ? format(dateRange.to, 'LLL d, yyyy') : ''

    return toStr ? `${fromStr} - ${toStr}` : fromStr
  }, [dateRange])

  const { data, isLoading, error } = useOrganizationTokenUsage(dateParams)

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Usage</h2>
          <p className="text-sm text-muted-foreground">
            Token consumption across all your agents.
          </p>
        </div>
        <DateRangePicker
          value={dateRange}
          onChange={setDateRange}
          align="end"
        />
      </div>

      {error ? (
        <div className="text-destructive text-sm">
          Failed to load token usage. Please try again.
        </div>
      ) : isLoading ? (
        <div className="flex items-center justify-center h-48">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          <div className="flex gap-4">
            <KPICard
              label="Total Tokens"
              value={data?.totals.totalTokens ?? 0}
              format={formatTokenCount}
              description="Input + output tokens consumed in this range"
            />
            <KPICard
              label="Input Tokens"
              value={data?.totals.inputTokens ?? 0}
              format={formatTokenCount}
              description="Prompt tokens sent to the model"
            />
            <KPICard
              label="Output Tokens"
              value={data?.totals.outputTokens ?? 0}
              format={formatTokenCount}
              description="Completion tokens generated by the model"
            />
          </div>

          <div className="h-[360px]">
            <TokensOverTimeChart
              data={data?.timeSeries.periods ?? []}
              dateRangeLabel={dateRangeLabel}
              height={320}
            />
          </div>

          {data && data.totals.totalTokens === 0 && (
            <p className="text-xs text-muted-foreground">
              No token usage in this range.
            </p>
          )}
        </>
      )}
    </div>
  )
}
